import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {AriaRole, CSSProperties, ReactNode} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {FlexboxAlign, FlexboxDirection, FlexboxJustify} from '../../types/Flexbox';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {getSize} from '../../utils/getSize';

export interface StackProps extends TestIdProps {
  children: ReactNode;
  direction: FlexboxDirection | ValueByDevice<FlexboxDirection>;
  spacing?: Integer | ValueByDevice<Integer | undefined>;
  align?: FlexboxAlign | ValueByDevice<FlexboxAlign | undefined>;
  justify?: FlexboxJustify | ValueByDevice<FlexboxJustify | undefined>;
  shrink?: number;
  grow?: number;
  basis?: CSSProperties['flexBasis'];
  width?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  height?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  maxWidth?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  maxHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  minWidth?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  minHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  wrap?: boolean | ValueByDevice<boolean | undefined> | 'nowrap';
  role?: AriaRole;
}

export function Stack(props: StackProps) {
  const align = useResponsivePropValue(props.align);
  const spacing = useResponsivePropValue(props.spacing);
  const direction = useResponsivePropValue(props.direction);
  const shouldWrap = useResponsivePropValue(props.wrap);
  const justify = useResponsivePropValue(props.justify);
  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);

  return (
    <div
      data-testid={suffixTestId('stack', props)}
      role={props.role}
      css={css`
        display: flex;
        width: ${getCssSize(width)};
        height: ${getCssSize(height)};
        max-width: ${getCssSize(maxWidth)};
        max-height: ${getCssSize(maxHeight)};
        min-width: ${getCssSize(minWidth)};
        min-height: ${getCssSize(minHeight)};
        flex-grow: ${props.grow ?? 'initial'};
        align-items: ${align ?? 'initial'};
        flex-shrink: ${props.shrink ?? 'initial'};
        flex-basis: ${props.basis ?? 'initial'};
        flex-direction: ${direction ?? 'row'};
        justify-content: ${justify ?? 'initial'};
        flex-wrap: ${match(shouldWrap)
          .with('nowrap', () => 'nowrap')
          .with(true, () => 'wrap')
          .otherwise(() => 'initial')};
        overflow: ${shouldWrap === 'nowrap' ? 'hidden' : 'initial'};
        gap: ${getSize(spacing ?? 0)};
      `}
    >
      {props.children}
    </div>
  );
}
